import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';

import style from 'styles/components/messageCenter/MessageCenterContainer';
import cn from 'classnames';

import { dismiss } from 'actions/dismissals';
import {
  batchUpdateMessages,
  updateMessage,
  messageInteraction,
  expireMessage,
  bootstrapMessageCenter,
} from 'actions/messageCenter';
import { unpause } from 'actions/user';
import { UNPAUSE_FROM_BANNER } from 'services/amplitude';

import * as fromDismissals from 'reducers/dismissals';
import * as fromMessageCenter from 'reducers/messageCenter';
import * as fromRoot from 'reducers';

import * as Types from 'utilities/types';
import * as Routes from 'helpers/routes';
import { ONBOARDING_MODAL_KEY } from 'helpers/dismissals';
import { DASHBOARD_V2_ROLLOUT, DASHBOARD_V2_VARIANT } from 'lib/constants';

import GlobalMessage from './GlobalMessage';
import DrawerMessagesContainer from './DrawerMessagesContainer';
import PromotionCarousel from './PromotionCarousel';
import Reward from './Reward';
import PauseMessage from './PauseMessage';

import IdPortal from 'components/common/IdPortal';
import BodyPortal from 'components/common/BodyPortal';
import Slideout, { WIDTH_500 } from 'components/common/Slideout';

import Bell from 'components/icons/Bell';
import CreditCard from 'components/icons/accent/CreditCard';
import Pause from 'components/icons/Pause';
import NewBadge from 'components/icons/NewBadge';

const MessageCenterContainer = ({
  needsInitializing,
  drawerMessages,
  unreadMessages,
  isMessageDismissed,
  globalMessage,
  onBootstrapMessageCenter,
  onDismiss,
  onUnpause,
  onUpdateMessages,
  onUpdateMessage,
  onExpireMessage,
  onClickMessage,
  hasViewedOnboarding,
  pauseMessage,
  rewardMessage,
  zoneZeroPromotions,
}) => {
  const [drawerShown, setDrawerShown] = useState(false);
  const currentPath = window.location.pathname;
  const getAction = action => {
    return {
      [Types.UNPAUSE_ACTION]: onUnpause,
    }[action];
  };

  const markReadOnClick = message => {
    const updateParams = { id: message.id, read: true };
    onClickMessage(message);
    if (!message.readAt) {
      onUpdateMessage(updateParams);
    }
  };
  const globalMessageAction = useCallback(() => {
    const actionFunc = getAction(globalMessage.action);
    if (!actionFunc) return () => {};
    return async () => {
      const response = await actionFunc();
      if (response.ok && !Routes.isDashboardPath(currentPath))
        window.location.assign(Routes.dashboardRootPath());
    };
  }, [get(globalMessage, 'heading', '')]);

  const globalMessageIcon = useCallback(() => {
    const iconClasses = 'hidden bp2:block mr-2';
    return {
      [Types.ICON_PAUSE]: <Pause className={iconClasses} />,
      [Types.ICON_CREDIT_CARD]: <CreditCard className={iconClasses} />,
      [Types.ICON_NEW]: <NewBadge className={iconClasses} />,
    }[globalMessage.icon];
  }, [get(globalMessage, 'icon', '')]);

  const onSetDrawerShown = useAnalyticsCallback(
    () => setDrawerShown(true),
    ({ logEvent, events }) => {
      logEvent(events.messageCenter.drawerShown);
    },
    [setDrawerShown]
  );

  const totalUnreadMessages = unreadMessages.length;

  const renderBell = () => (
    <button
      onClick={onSetDrawerShown}
      className={cn(style.iconPadding, 'border-none appearance-none relative')}
    >
      <Bell />
      {totalUnreadMessages > 0 && <span className={style.circle}>{totalUnreadMessages}</span>}
    </button>
  );

  useEffect(() => {
    const onInitialize = async () => await onBootstrapMessageCenter();
    if (needsInitializing) onInitialize();
  }, [needsInitializing]);

  const renderDashboardCard = () => {
    if (document.getElementById('promotion-portal') && hasViewedOnboarding) {
      if (
        rewardMessage &&
        // eslint-disable-next-line no-extra-boolean-cast
        !Boolean(rewardMessage.readAt) &&
        !currentPath.includes(Routes.OUR_MENU_PATH)
      ) {
        return (
          <IdPortal id="promotion-portal">
            <Reward {...rewardMessage} onUpdateMessage={onUpdateMessage} />
          </IdPortal>
        );
      }
      return (
        <IdPortal id="promotion-portal">
          <PromotionCarousel
            isMessageDismissed={isMessageDismissed}
            zoneZeroPromotions={zoneZeroPromotions}
            onDismiss={onDismiss}
          />
        </IdPortal>
      );
    }
  };

  return (
    <React.Fragment>
      {pauseMessage ? (
        <PauseMessage {...globalMessage} actionFunction={globalMessageAction()} />
      ) : (
        globalMessage && (
          <GlobalMessage
            {...globalMessage}
            icon={globalMessageIcon()}
            actionFunction={globalMessageAction()}
            messageStyle="error"
          />
        )
      )}
      <IdPortal id="message-center-bell-mobile-portal">{renderBell()}</IdPortal>
      <IdPortal id="message-center-bell-tbl-portal">{renderBell()}</IdPortal>
      <IdPortal id="message-center-bell-web-portal">{renderBell()}</IdPortal>
      <BodyPortal>
        <Slideout
          id="message-center-modal-dialog"
          labelId="message-center-label"
          show={drawerShown}
          closeCallback={() => setDrawerShown(false)}
          desktopWidth={WIDTH_500}
        >
          <h1 id="message-center-label" className="h3 mx-6 mt-4">
            Messages
          </h1>
          <DrawerMessagesContainer
            messages={drawerMessages}
            onUpdateMessages={onUpdateMessages}
            onExpireMessage={onExpireMessage}
            onClickMessage={markReadOnClick}
          />
        </Slideout>
      </BodyPortal>
      {renderDashboardCard()}
    </React.Fragment>
  );
};

MessageCenterContainer.propTypes = {
  globalMessage: Types.message,
  rewardMessage: Types.message,
  pauseMessage: Types.message,
  drawerMessages: PropTypes.arrayOf(Types.message).isRequired,
  unreadMessages: PropTypes.arrayOf(Types.message).isRequired,
  isMessageDismissed: PropTypes.func.isRequired,
  promotionMessage: Types.message,
  onClickMessage: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  needsInitializing: PropTypes.bool.isRequired,
  onBootstrapMessageCenter: PropTypes.func.isRequired,
  onUpdateMessages: PropTypes.func.isRequired,
  onUpdateMessage: PropTypes.func.isRequired,
  onUnpause: PropTypes.func.isRequired,
  hasViewedOnboarding: PropTypes.bool.isRequired,
  zoneZeroPromotions: PropTypes.array.isRequired,
};

MessageCenterContainer.defaultProps = {
  globalMessage: null,
  rewardMessage: null,
  pauseMessage: null,
};

export default connect(
  state => {
    const dashboardVariant = fromRoot.selectExperimentVariant(state, DASHBOARD_V2_ROLLOUT);

    return {
      globalMessage: fromMessageCenter.selectMessageByPlacementAndPriority(state, 'global'),
      drawerMessages: fromMessageCenter.selectOrderedDrawerMessages(state),
      rewardMessage: fromMessageCenter.selectRewardMessage(state),
      pauseMessage: fromMessageCenter.pauseMessage(state),
      unreadMessages: fromMessageCenter.selectUnreadDrawerMessages(state),
      isMessageDismissed: key => fromDismissals.selectIsDismissed(state, key),
      needsInitializing: fromMessageCenter.needsInitializing(state),
      user: fromRoot.selectUser(state),
      hasViewedOnboarding:
        dashboardVariant === DASHBOARD_V2_VARIANT ||
        fromDismissals.selectIsDismissed(state, ONBOARDING_MODAL_KEY),
      zoneZeroPromotions: fromMessageCenter.selectZoneZeroPromotions(state),
    };
  },
  {
    onBootstrapMessageCenter: bootstrapMessageCenter,
    onDismiss: dismiss,
    onUnpause: () => unpause(UNPAUSE_FROM_BANNER),
    onUpdateMessages: batchUpdateMessages,
    onUpdateMessage: updateMessage,
    onClickMessage: messageInteraction,
    onExpireMessage: expireMessage,
  }
)(MessageCenterContainer);
